const browserOrigin = typeof window !== 'undefined' ? window.location.origin : '';

const readEnv = (value?: string) => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) return '';
  if (trimmed.startsWith('{{') && trimmed.endsWith('}}')) return '';
  // An Azure Pipelines macro left unexpanded because its variable is not defined.
  if (trimmed.startsWith('$(') && trimmed.endsWith(')')) return '';

  return trimmed;
};

const buildBrowserUrl = (value?: string) => {
  const configuredUrl = readEnv(value);

  if (!configuredUrl) return browserOrigin;
  if (configuredUrl.startsWith('/') && browserOrigin) return `${browserOrigin}${configuredUrl}`;

  return configuredUrl;
};

const buildAuthorizationParams = (audience: string, scopes: string[]) => ({
  ...(audience ? { audience } : {}),
  ...(scopes.length ? { scope: scopes.join(' ') } : {}),
});

const mergeIdentityScopes = (configuredScope: string) => Array.from(new Set([
  'openid',
  'profile',
  'email',
  ...configuredScope
    .split(' ')
    .map((scope) => scope.trim())
    .filter(Boolean),
]));

export const auth0Domain = readEnv(import.meta.env.VITE_AUTH0_DOMAIN);
export const auth0ClientId = readEnv(import.meta.env.VITE_AUTH0_CLIENT_ID);
export const auth0RedirectUri = buildBrowserUrl(import.meta.env.VITE_AUTH0_REDIRECT_URI);
export const auth0PostLogoutRedirectUri = buildBrowserUrl(import.meta.env.VITE_AUTH0_POST_LOGOUT_REDIRECT_URI);
export const auth0ManagementAudience = readEnv(import.meta.env.VITE_AUTH0_AUDIENCE);
export const auth0ManagementScope = readEnv(import.meta.env.VITE_AUTH0_SCOPE);
export const auth0DiAudience = readEnv(import.meta.env.VITE_AUTH0_DI_AUDIENCE);
export const auth0DiScope = readEnv(import.meta.env.VITE_AUTH0_DI_SCOPE);
export const auth0ScAudience = readEnv(import.meta.env.VITE_AUTH0_SC_AUDIENCE);
export const auth0ScScope = readEnv(import.meta.env.VITE_AUTH0_SC_SCOPE);
export const auth0SupportAudience = readEnv(import.meta.env.VITE_AUTH0_SUPPORT_AUDIENCE);
export const auth0NotificationsAudience = readEnv(import.meta.env.VITE_AUTH0_NOTIFICATIONS_AUDIENCE);

const managementScopes = mergeIdentityScopes(auth0ManagementScope);

export const auth0ManagementApiAuthorizationParams = buildAuthorizationParams(auth0ManagementAudience, managementScopes);
export const auth0DefaultApiAuthorizationParams = auth0ManagementApiAuthorizationParams;
export const auth0ApiAuthorizationParams = auth0ManagementApiAuthorizationParams;

export const hasAuth0DiApiAuthorizationParams = Boolean(auth0DiAudience || auth0DiScope);
export const auth0DiApiAuthorizationParams = hasAuth0DiApiAuthorizationParams
  ? buildAuthorizationParams(auth0DiAudience, mergeIdentityScopes(auth0DiScope))
  : {};
export const auth0DiCompatibleApiAuthorizationParams = hasAuth0DiApiAuthorizationParams
  ? auth0DiApiAuthorizationParams
  : auth0ManagementApiAuthorizationParams;

// SC intentionally uses the shared Management audience when no explicit SC token parameters
// are configured, matching Zespy.Management.Client's declared default. SC tenant access is enabled;
// the customer explanation endpoint verifies authenticated tenant access and the zespy-sc entitlement.
// A separate SC API registration is not a prerequisite for this shared audience lane. Preserve
// explicit SC parameters for environments that configure them; the compatibility logic stays unchanged.
export const hasAuth0ScApiAuthorizationParams = Boolean(auth0ScAudience || auth0ScScope);
export const auth0ScApiAuthorizationParams = hasAuth0ScApiAuthorizationParams
  ? buildAuthorizationParams(auth0ScAudience, mergeIdentityScopes(auth0ScScope))
  : {};
export const auth0ScCompatibleApiAuthorizationParams = hasAuth0ScApiAuthorizationParams
  ? auth0ScApiAuthorizationParams
  : auth0ManagementApiAuthorizationParams;

// Support and Notifications accept only a token for their own Auth0 API, which the portal's refresh
// token covers through Auth0 multi-resource refresh tokens (MRRT). They have no Management fallback:
// without an audience the lane is undefined and its requests fail closed. The APIs define no scopes.
const buildOwnAudienceAuthorizationParams = (audience: string) => audience
  ? buildAuthorizationParams(audience, mergeIdentityScopes(''))
  : undefined;

export const auth0SupportApiAuthorizationParams = buildOwnAudienceAuthorizationParams(auth0SupportAudience);
export const auth0NotificationsApiAuthorizationParams = buildOwnAudienceAuthorizationParams(auth0NotificationsAudience);

// MRRT, with no hidden-iframe fallback, only in a build that has an own-API lane to serve. It needs the
// tenant to issue the portal a refresh token at sign-in (the Management API allows offline access) and
// to list each own API in the client's refresh-token policies. A build without an own-API audience
// renews tokens as before: refresh tokens, falling back to the iframe.
export const auth0UsesMultiResourceRefreshTokens = Boolean(auth0SupportAudience || auth0NotificationsAudience);

export const auth0ProviderAuthorizationParams = {
  redirect_uri: auth0RedirectUri,
  ...auth0ApiAuthorizationParams,
};

export const auth0LogoutParams = {
  returnTo: auth0PostLogoutRedirectUri,
};

export const hasAuth0Config = Boolean(auth0Domain && auth0ClientId);

export type AuthProviderMode = 'auth0' | 'missing';

export const authProviderMode: AuthProviderMode = hasAuth0Config ? 'auth0' : 'missing';